import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  Table,
  Thead,
  Tbody,
  SimpleGrid,
  Tr,
  Th,
  Td,
  Text,
  Button,
  HStack,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Stack,
  ModalFooter,
  Icon,
  Input,
  useToast,
} from "@chakra-ui/react";
import {
  FaSync,
  FaPrint,
  FaEdit,
  FaTrash,
  FaFileDownload,
  FaFileUpload,
  FaClipboard,
  FaPlus,
  FaChalkboardTeacher,
  FaBook,
  FaHeading,
} from "react-icons/fa";
import { BASE_URL } from "../../Globalapi/Globalapi";

const View_Homework = () => {
  // State Management
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [homework, setHomework] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const userid = sessionStorage.getItem("user_id");
  const toast = useToast();

  // Initial Data Fetching
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [classesResponse, subjectsResponse] = await Promise.all([
          axios.get(`${BASE_URL}/admin/class/class_master.php`),
          axios.get(
            `${BASE_URL}/admin/subject/list_subject.php`
          ),
        ]);

        if (classesResponse.data.data) {
          setClasses(classesResponse.data.data);
        } else {
          console.error("No classes data received");
          toast({
            title: "Warning",
            description: "Could not load classes",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
        }

        if (subjectsResponse.data.data) {
          setSubjects(subjectsResponse.data.data);
        } else {
          console.error("No subjects data received");
          toast({
            title: "Warning",
            description: "Could not load subjects",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast({
          title: "Error",
          description: "Failed to load initial data",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchInitialData();
  }, [toast]);

  // Fetch Homework Data
  useEffect(() => {
    const fetchHomework = async () => {
      try {
        const response = await axios.post(
          `${BASE_URL}/staff/homework/list_all_homework.php`,
          { pass: userid }
        );

        if (response.data.success && response.data.data) {
          setHomework(response.data.data);
        } else {
          setHomework([]);
          console.error("No homework data received:", response.data.message);
          toast({
            title: "Info",
            description: "No homework data available",
            status: "info",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching homework:", error);
        toast({
          title: "Error",
          description: "Failed to load homework data",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    if (userid) {
      fetchHomework();
    }
  }, [userid, toast]);

  // Filtered Data using useMemo for performance
  const filteredHomework = React.useMemo(() => {
    return homework.filter((item) => {
      const classMatch =
        !selectedClass || String(item.class_id) === String(selectedClass);
      const subjectMatch =
        !selectedSubject || String(item.subject_id) === String(selectedSubject);
      return classMatch && subjectMatch;
    });
  }, [homework, selectedClass, selectedSubject]);

  // Pagination Calculations
  const totalPages = Math.ceil(filteredHomework.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const currentData = filteredHomework.slice(
    startIndex,
    startIndex + entriesPerPage
  );

  // Event Handlers
  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
    setCurrentPage(1);
  };

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
    setCurrentPage(1);
  };

  const handleEdit = (id) => {
    const materialToEdit = homework.find((material) => material.id === id);
    if (materialToEdit) {
      setEditData({
        ...materialToEdit,
        class_id: String(materialToEdit.class_id),
        subject_id: String(materialToEdit.subject_id),
      });
      setEditModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setEditData(null);
  };

  const handleSaveChanges = async () => {
    try {
      let fileBase64 = null;
      if (editData.newFile) {
        fileBase64 = await toBase64(editData.newFile);
      }

      const postData = {
        homework_id: editData.id,
        class_id: editData.class_id,
        subject_id: editData.subject_id,
        title: editData.title,
        description: editData.description,
        pass: userid,
        material_file: fileBase64,
      };

      const response = await axios.put(
        `${BASE_URL}/staff/homework/edit_homework.php`,
        postData
      );

      if (response.data.success) {
        // Update local state
        const updatedHomework = homework.map((item) =>
          item.id === editData.id ? { ...item, ...editData } : item
        );
        setHomework(updatedHomework);

        handleModalClose();
        toast({
          title: "Success",
          description: "Homework updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(response.data.message || "Failed to update homework");
      }
    } catch (error) {
      console.error("Error updating homework:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update homework",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this homework?")) {
      return;
    }

    try {
      const response = await axios.delete(
        `${BASE_URL}/staff/homework/delete_homework.php`,
        {
          data: { homework_id: id, pass: userid },
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.data.success) {
        setHomework(homework.filter((item) => item.id !== id));
        toast({
          title: "Success",
          description: "Homework deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(response.data.message || "Failed to delete homework");
      }
    } catch (error) {
      console.error("Error deleting homework:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete homework",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // Component Render
  return (
    <Box>
      <div style={{ backgroundColor: "#FBFBFD", minHeight: "100vh" }}>
        <div className="teacher-list-container">
          <div className="header">
            <h1 style={{ fontSize: "20px", margin: 0 }}>View Homework</h1>
            <HStack spacing={4} mt={2}>
              <Button
                className="refresh-button"
                boxShadow="md"
                backgroundColor="white"
                fontSize="11px"
                onClick={() => window.location.reload()}
              >
                <FaSync color="#515B73" />
              </Button>
              <Button
                className="export-button"
                backgroundColor="white"
                fontSize="11px"
                boxShadow="md"
                onClick={() => window.print()}
              >
                <FaPrint color="#515B73" />
                Print PDF
              </Button>
            </HStack>
          </div>

          <div className="breadcrumb">
            <span>Staff</span> / <span>Course Material</span> /{" "}
            <span>View Homework</span>
          </div>
        </div>

        <Box p={6}>
          <Flex
            mt={4}
            justifyContent="space-between"
            alignItems="center"
            className="sb"
            p={3}
            direction={{ base: "column", md: "row" }} // Stack elements vertically on mobile
          >
            <Flex
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              wrap="wrap"
            >
              <Text mr={2} whiteSpace="nowrap">
                Rows Per Page
              </Text>
              <FormControl id="entries" width="auto">
                <Select
                  width={{ base: "220px", md: "150px" }}
                  value={entriesPerPage}
                  onChange={(e) => {
                    setEntriesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="10">10</option>
                  <option value="50">50</option>
                  <option value="70">70</option>
                </Select>
              </FormControl>
            </Flex>

            <Flex
              width={{ base: "100%", md: "48%" }}
              justifyContent="space-between"
              mt={{ base: 3, md: 0 }} // Add margin-top for mobile
              direction={{ base: "column", md: "row" }} // Stack vertically on mobile
            >
              <FormControl id="class" width="100%">
                <Select
                  placeholder="Select Class"
                  value={selectedClass}
                  onChange={handleClassChange}
                >
                  <option value="">All Classes</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.class}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                id="subject"
                width="100%"
                mt={{ base: 3, md: 0 }}
                ml={{ md: 2 }}
              >
                <Select
                  placeholder="Select Subject"
                  value={selectedSubject}
                  onChange={handleSubjectChange}
                >
                  <option value="">All Subjects</option>
                  {subjects.map((subject) => (
                    <option key={subject.subject_id} value={subject.subject_id}>
                      {subject.subject}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Flex>
          </Flex>

          <Box overflowX="auto" maxWidth="100%">
            <Table
              sx={{
                "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
              }}
            >
              <Thead>
                <Tr>
                  <Th>Class</Th>
                  <Th>Subject</Th>
                  <Th>Caption</Th>
                  <Th>Description</Th>
                  <Th>Homework File</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentData.length > 0 ? (
                  currentData.map((material) => (
                    <Tr key={material.id}>
                      <Td>{material.class || "N/A"}</Td>
                      <Td>{material.subject || "N/A"}</Td>
                      <Td>{material.title || "N/A"}</Td>
                      <Td>{material.description || "N/A"}</Td>
                      <Td>
                        {material.attachment_url ? (
                          <a
                            href={material.attachment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View File
                          </a>
                        ) : (
                          "No File"
                        )}
                      </Td>
                      <Td>
                        <HStack>
                          <Button
                            onClick={() => handleEdit(material.id)}
                            variant="link"
                            colorScheme="teal"
                            size="sm"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            onClick={() => handleDelete(material.id)}
                            variant="link"
                            colorScheme="red"
                            size="sm"
                          >
                            <FaTrash />
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan="6">
                      <Text textAlign="center">No homework available</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          {filteredHomework.length > 0 && (
            <Flex justify="end" align="center" p={3} className="sb2">
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
              >
                Prev
              </Button>

              <Stack direction="row" spacing={2} mx={2}>
                {Array.from({ length: totalPages }, (_, i) => (
                  <Button
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    colorScheme={currentPage === i + 1 ? "teal" : "gray"}
                  >
                    {i + 1}
                  </Button>
                ))}
              </Stack>

              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Flex>
          )}
        </Box>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleModalClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            {" "}
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Edit Homework
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            {editData && (
              <>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl mb={4}>
                    <FormLabel>
                      {" "}
                      <Icon as={FaChalkboardTeacher} mr={1} color="teal.500" />
                      Class
                    </FormLabel>
                    <Select
                      bg="white"
                      _hover={{ borderColor: "teal.300" }}
                      placeholder="Select Class"
                      value={editData.class_id || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, class_id: e.target.value })
                      }
                    >
                      {classes.map((cls) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.class}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl mb={4}>
                    <FormLabel>
                      {" "}
                      <Icon as={FaBook} mr={1} color="teal.500" />
                      Subject
                    </FormLabel>
                    <Select
                      bg="white"
                      _hover={{ borderColor: "teal.300" }}
                      placeholder="Select Subject"
                      value={editData.subject || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, subject_id: e.target.value })
                      }
                    >
                      {subjects.map((subject) => (
                        <option
                          key={subject.subject_id}
                          value={subject.subject_id}
                        >
                          {subject.subject}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl mb={4}>
                    <FormLabel>
                      {" "}
                      <Icon as={FaHeading} mr={1} color="teal.500" />
                      Title
                    </FormLabel>
                    <Input
                      bg="white"
                      _hover={{ borderColor: "teal.300" }}
                      type="text"
                      value={editData.title || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, title: e.target.value })
                      }
                    />
                  </FormControl>
                  <FormControl mb={4}>
                    <FormLabel>
                      {" "}
                      <Icon as={FaClipboard} mr={1} color="teal.500" />
                      Description
                    </FormLabel>
                    <Input
                      type="text"
                      bg="white"
                      _hover={{ borderColor: "teal.300" }}
                      value={editData.description || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          description: e.target.value,
                        })
                      }
                    />
                  </FormControl>

                  {/* Download existing file if available */}
                  {editData.attachment_url && (
                    <FormControl mb={4}>
                      <FormLabel>
                        {" "}
                        <Icon as={FaFileDownload} mr={1} color="teal.500" />
                        Existing File
                      </FormLabel>
                      <a
                        href={editData.attachment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "blue", textDecoration: "underline" }}
                      >
                        Download Current File
                      </a>
                    </FormControl>
                  )}

                  {/* File Upload Input */}
                  <FormControl mb={4}>
                    <FormLabel>
                      {" "}
                      <Icon as={FaFileUpload} mr={1} color="teal.500" />
                      Upload New File
                    </FormLabel>
                    <Input
                      bg="white"
                      _hover={{ borderColor: "teal.300" }}
                      type="file"
                      accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) =>
                        setEditData({ ...editData, newFile: e.target.files[0] })
                      }
                    />
                  </FormControl>
                </SimpleGrid>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {/* <Button onClick={handleModalClose} mr={3}>
              Cancel
            </Button> */}
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleSaveChanges}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default View_Homework;
